import type { Component } from "vue";
import { FileText, Image as ImageIcon, Mail, Presentation, Sheet, Type } from "lucide-vue-next";
import type { SurveyContextSource } from "@/types";

// Mirror SurveyContexts::SourceInput::ACCEPTED_EXTENSIONS, Storages::Validations::SurveyContextFiles::MAX_SIZE
// and SurveyContextSource::MAX_SOURCES in the API.
export const MAX_FILE_SIZE = 50 * 1024 * 1024;
export const MAX_SOURCES = 50;
export const ACCEPTED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "ppt",
  "pptx",
  "odt",
  "odp",
  "rtf",
  "xls",
  "xlsx",
  "ods",
  "csv",
  "tsv",
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
  "heic",
  "tif",
  "tiff",
  "txt",
  "md",
  "markdown",
  "html",
  "htm",
  "xml",
  "json",
  "eml",
  "msg",
];
export const ACCEPTED_FAMILIES = "PDF, Office, OpenDocument, images, text and mail files";
export const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.map((extension) => `.${extension}`).join(",");

const extensionOf = (name: string) => (name.includes(".") ? name.split(".").pop()?.toLowerCase() ?? "" : "");

export const isAccepted = (file: File) => ACCEPTED_EXTENSIONS.includes(extensionOf(file.name));

type SourceFormat = { label: string; icon: Component; class: string };

const NEUTRAL = "bg-secondary text-foreground-secondary";
const PDF: SourceFormat = { label: "PDF", icon: FileText, class: "bg-status-error-soft text-status-error-strong" };
const WORD: SourceFormat = { label: "Word", icon: FileText, class: "bg-status-info-soft text-status-info-strong" };
const POWERPOINT: SourceFormat = {
  label: "PowerPoint",
  icon: Presentation,
  class: "bg-status-warning-soft text-status-warning-strong",
};
const EXCEL: SourceFormat = { label: "Excel", icon: Sheet, class: "bg-status-success-soft text-status-success-strong" };
const CSV: SourceFormat = { label: "CSV", icon: Sheet, class: "bg-status-success-soft text-status-success-strong" };
const TEXT: SourceFormat = { label: "Text", icon: FileText, class: NEUTRAL };
const IMAGE: SourceFormat = { label: "Image", icon: ImageIcon, class: NEUTRAL };
const EMAIL: SourceFormat = { label: "Email", icon: Mail, class: NEUTRAL };
const PASTED_TEXT: SourceFormat = {
  label: "Pasted text",
  icon: Type,
  class: "bg-status-info-soft text-status-info-strong",
};

const FORMATS: Record<string, SourceFormat> = {
  pdf: PDF,
  doc: WORD,
  docx: WORD,
  odt: { ...WORD, label: "OpenDocument" },
  rtf: { ...WORD, label: "RTF" },
  ppt: POWERPOINT,
  pptx: POWERPOINT,
  odp: { ...POWERPOINT, label: "OpenDocument" },
  xls: EXCEL,
  xlsx: EXCEL,
  ods: { ...EXCEL, label: "OpenDocument" },
  csv: CSV,
  tsv: { ...CSV, label: "TSV" },
  txt: TEXT,
  md: { ...TEXT, label: "Markdown" },
  markdown: { ...TEXT, label: "Markdown" },
  html: { ...TEXT, label: "HTML" },
  htm: { ...TEXT, label: "HTML" },
  xml: { ...TEXT, label: "XML" },
  json: { ...TEXT, label: "JSON" },
  png: IMAGE,
  jpg: IMAGE,
  jpeg: IMAGE,
  webp: IMAGE,
  gif: IMAGE,
  heic: IMAGE,
  tif: IMAGE,
  tiff: IMAGE,
  eml: EMAIL,
  msg: EMAIL,
};

export function sourceFormat(source: SurveyContextSource): SourceFormat {
  if (source.kind === "text") return PASTED_TEXT;

  const extension = extensionOf(source.name);
  return FORMATS[extension] ?? { label: extension ? extension.toUpperCase() : "File", icon: FileText, class: NEUTRAL };
}
